import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Save, Mail, Phone, MapPin, Clock, CheckCircle2, AlertCircle, MessageSquare, Wrench, Calculator, RefreshCw, Database, ChevronUp, ChevronDown, Briefcase, Image, Copy, FileText, BarChart3, ShoppingCart } from 'lucide-react';
import CalculatorManager from '../components/admin/CalculatorManager';
import PortfolioManager from '../components/admin/PortfolioManager';
import DevizeManager from '../components/admin/DevizeManager';
import StatisticsManager from '../components/admin/StatisticsManager';
import OrdersManager from '../components/admin/OrdersManager';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [roofTypes, setRoofTypes] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [services, setServices] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [dbStats, setDbStats] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [showPortfolioDialog, setShowPortfolioDialog] = useState(false);
  const [newPortfolioItem, setNewPortfolioItem] = useState({
    title: '',
    description: '',
    client_name: '',
    location: '',
    images: ['']
  });
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState(null);

  useEffect(() => {
    if (!user || !user.is_admin) {
      toast.error('Acces interzis. Doar adminii pot accesa această pagină.');
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [materialsRes, productsRes, roofTypesRes, quoteRequestsRes, servicesRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/materials`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/roof-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/quote-requests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setMaterials(materialsRes.data);
      setProducts(productsRes.data);
      setRoofTypes(roofTypesRes.data);
      setQuoteRequests(quoteRequestsRes.data);
      setServices(servicesRes.data);
      setPortfolio(portfolioRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Eroare la încărcarea datelor');
    }
  };

  const handleSaveMaterial = async (materialData) => {
    try {
      if (editingMaterial) {
        await axios.put(`${API}/admin/materials/${editingMaterial.id}`, materialData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material actualizat cu succes');
      } else {
        await axios.post(`${API}/admin/materials`, materialData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Material adăugat cu succes');
      }
      setShowMaterialDialog(false);
      setEditingMaterial(null);
      fetchData();
    } catch (error) {
      console.error('Error saving material:', error);
      toast.error('Eroare la salvarea materialului');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest material?')) return;
    
    try {
      await axios.delete(`${API}/admin/materials/${materialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Material șters cu succes');
      fetchData();
    } catch (error) {
      console.error('Error deleting material:', error);
      toast.error('Eroare la ștergerea materialului');
    }
  };

  const handleDuplicateMaterial = async (materialId, materialName) => {
    try {
      const response = await axios.post(`${API}/admin/materials/${materialId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Material duplicat: ${response.data.name}`);
      fetchData();
    } catch (error) {
      console.error('Error duplicating material:', error);
      toast.error('Eroare la duplicarea materialului');
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produs actualizat cu succes');
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produs adăugat cu succes');
      }
      setShowProductDialog(false);
      setEditingProduct(null);
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Eroare la salvarea produsului');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Sigur doriți să ștergeți acest produs?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produs șters cu succes');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Eroare la ștergerea produsului');
    }
  };

  const handleUpdateQuoteStatus = async (requestId, newStatus) => {
    try {
      await axios.put(`${API}/admin/quote-requests/${requestId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status actualizat cu succes');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Eroare la actualizarea statusului');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-yellow-100 text-yellow-700';
      case 'quoted': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Nou';
      case 'contacted': return 'Contactat';
      case 'quoted': return 'Ofertă trimisă';
      case 'completed': return 'Finalizat';
      default: return status;
    }
  };

  const handleSaveService = async (serviceData) => {
    try {
      await axios.put(`${API}/admin/services/${serviceData.id}`, serviceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Serviciu actualizat cu succes');
      setShowServiceDialog(false);
      setEditingService(null);
      fetchData();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Eroare la salvarea serviciului');
    }
  };

  const handleDeleteService = async (serviceId, serviceName) => {
    if (!window.confirm(`Sigur doriți să ștergeți serviciul "${serviceName}"?\n\nAceastă acțiune este ireversibilă!`)) return;
    
    try {
      await axios.delete(`${API}/admin/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Serviciu șters cu succes');
      fetchData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Eroare la ștergerea serviciului');
    }
  };

  const handleMoveService = async (serviceId, direction) => {
    const currentIndex = services.findIndex(s => s.id === serviceId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= services.length) return;
    
    // Swap order values
    const currentService = services[currentIndex];
    const swapService = services[newIndex];
    
    try {
      // Update both services with swapped order values
      await axios.put(`${API}/admin/services/${currentService.id}`, 
        { ...currentService, order: swapService.order || newIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await axios.put(`${API}/admin/services/${swapService.id}`, 
        { ...swapService, order: currentService.order || currentIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ordine actualizată');
      fetchData();
    } catch (error) {
      console.error('Error reordering services:', error);
      toast.error('Eroare la reordonare');
    }
  };

  // Portfolio functions
  const handleAddPortfolioItem = async () => {
    if (!newPortfolioItem.title) {
      toast.error('Introduceți titlul proiectului');
      return;
    }
    
    // Filter out empty image URLs
    const images = newPortfolioItem.images.filter(img => img.trim() !== '');
    if (images.length === 0) {
      toast.error('Adăugați cel puțin o imagine');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/portfolio`, {
        ...newPortfolioItem,
        images: images
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proiect adăugat cu succes');
      setShowPortfolioDialog(false);
      setNewPortfolioItem({ title: '', description: '', client_name: '', location: '', images: [''] });
      fetchData();
    } catch (error) {
      console.error('Error adding portfolio item:', error);
      toast.error('Eroare la adăugarea proiectului');
    }
  };

  const handleDeletePortfolioItem = async (itemId, itemTitle) => {
    if (!window.confirm(`Sigur doriți să ștergeți proiectul "${itemTitle}"?\n\nAceastă acțiune este ireversibilă!`)) return;
    
    try {
      await axios.delete(`${API}/admin/portfolio/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proiect șters cu succes');
      fetchData();
    } catch (error) {
      console.error('Error deleting portfolio item:', error);
      toast.error('Eroare la ștergerea proiectului');
    }
  };

  const addImageField = () => {
    setNewPortfolioItem(prev => ({
      ...prev,
      images: [...prev.images, '']
    }));
  };

  const updateImageField = (index, value) => {
    setNewPortfolioItem(prev => ({
      ...prev,
      images: prev.images.map((img, i) => i === index ? value : img)
    }));
  };

  const removeImageField = (index) => {
    setNewPortfolioItem(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-cyan-50" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-title">
              Admin Dashboard
            </h1>
            <p className="text-lg text-slate-600 mb-4">Gestionează materiale, produse și portofoliu</p>
            
            {/* Database Sync Button */}
            <div className="flex justify-center gap-4 items-center">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await axios.get(`${API}/admin/database-stats`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setDbStats(response.data);
                    toast.info(`Produse: ${response.data.products}, Servicii: ${response.data.services}, Materiale: ${response.data.materials}`);
                  } catch (error) {
                    toast.error('Eroare la verificare');
                  }
                }}
                data-testid="check-db-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                Verifică Date
              </Button>
              
              <Button
                onClick={async () => {
                  if (!window.confirm('Sincronizezi baza de date? Aceasta va adăuga produsele, serviciile și materialele lipsă.')) return;
                  
                  setSyncLoading(true);
                  try {
                    const response = await axios.post(`${API}/admin/sync-database`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Baza de date sincronizată cu succes!');
                    console.log('Sync results:', response.data.results);
                    // Reload data
                    window.location.reload();
                  } catch (error) {
                    console.error('Sync error:', error);
                    toast.error('Eroare la sincronizare: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setSyncLoading(false);
                  }
                }}
                disabled={syncLoading}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="sync-db-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? 'Se sincronizează...' : 'Sincronizează Date'}
              </Button>
            </div>
            
            {dbStats && (
              <div className="mt-4 text-sm text-slate-500">
                Produse: {dbStats.products} | Servicii: {dbStats.services} | Materiale: {dbStats.materials} | Faze lucru: {dbStats.work_phases}
              </div>
            )}
          </div>

          <Tabs defaultValue="orders" className="glass-card p-3 sm:p-6">
            <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-1">
              <TabsTrigger value="orders" data-testid="tab-orders" className="text-xs sm:text-sm">
                <ShoppingCart className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Comenzi</span>
              </TabsTrigger>
              <TabsTrigger value="statistics" data-testid="tab-statistics" className="text-xs sm:text-sm">
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Statistici</span>
              </TabsTrigger>
              <TabsTrigger value="quote-requests" data-testid="tab-quotes" className="text-xs sm:text-sm">
                <MessageSquare className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Cereri</span>
                <span className="ml-1">({quoteRequests.filter(q => q.status === 'new').length})</span>
              </TabsTrigger>
              <TabsTrigger value="devize" data-testid="tab-devize" className="text-xs sm:text-sm">
                <FileText className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Devize</span>
              </TabsTrigger>
              <TabsTrigger value="calculators" data-testid="tab-calculators" className="text-xs sm:text-sm">
                <Calculator className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Calc.</span>
              </TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services" className="text-xs sm:text-sm">
                <Wrench className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Servicii</span>
              </TabsTrigger>
              <TabsTrigger value="portfolio" data-testid="tab-portfolio" className="text-xs sm:text-sm">
                <Briefcase className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Portofoliu</span>
              </TabsTrigger>
              <TabsTrigger value="materials" data-testid="tab-materials" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Materiale</span>
                <span className="sm:hidden">Mat.</span>
              </TabsTrigger>
              <TabsTrigger value="products" data-testid="tab-products" className="text-xs sm:text-sm">
                <span className="hidden sm:inline">Produse</span>
                <span className="sm:hidden">Prod.</span>
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <OrdersManager />
            </TabsContent>

            {/* Statistics Tab */}
            <TabsContent value="statistics">
              <StatisticsManager />
            </TabsContent>

            {/* Devize Manager Tab */}
            <TabsContent value="devize">
              <DevizeManager token={token} />
            </TabsContent>

            {/* Calculator Manager Tab */}
            <TabsContent value="calculators">
              <CalculatorManager token={token} />
            </TabsContent>

            {/* Quote Requests Tab */}
            <TabsContent value="quote-requests">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Cereri de Ofertă
                </h2>
                <p className="text-slate-600">Gestionează cererile primite prin formularul VREAU OFERTĂ</p>
              </div>

              {quoteRequests.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Nu există cereri de ofertă momentan.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quoteRequests.map((request) => (
                    <div 
                      key={request.id} 
                      className={`border rounded-xl p-6 transition ${
                        request.status === 'new' ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'
                      }`}
                      data-testid={`quote-${request.id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{request.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                            <span className="flex items-center"><Mail className="w-4 h-4 mr-1" />{request.email}</span>
                            <span className="flex items-center"><Phone className="w-4 h-4 mr-1" />{request.phone}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(request.status)}`}>
                            {getStatusLabel(request.status)}
                          </span>
                          <Select 
                            value={request.status} 
                            onValueChange={(value) => handleUpdateQuoteStatus(request.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">Nou</SelectItem>
                              <SelectItem value="contacted">Contactat</SelectItem>
                              <SelectItem value="quoted">Ofertă trimisă</SelectItem>
                              <SelectItem value="completed">Finalizat</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Tip construcție:</span>
                          <p className="font-medium">{request.constructionTypeLabel}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Locație:</span>
                          <p className="font-medium">{request.locationLabel}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Suprafață:</span>
                          <p className="font-medium">{request.surfaceLabel}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Data cererii:</span>
                          <p className="font-medium flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {new Date(request.created_at).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t">
                        <div className="mb-2">
                          <span className="text-slate-500 text-sm">Zone:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {request.zonesLabels?.map((zone, i) => (
                              <span key={i} className="px-2 py-1 bg-slate-100 rounded text-sm">{zone}</span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-500 text-sm">Tipuri izolație:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {request.isolationTypesLabels?.map((type, i) => (
                              <span key={i} className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-sm">{type}</span>
                            ))}
                          </div>
                        </div>
                        {request.additionalInfo && (
                          <div className="mt-3">
                            <span className="text-slate-500 text-sm">Informații suplimentare:</span>
                            <p className="mt-1 text-slate-700 bg-slate-50 p-3 rounded">{request.additionalInfo}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Servicii
                  </h2>
                  <p className="text-slate-600">Gestionează serviciile afișate pe site</p>
                </div>
              </div>

              <div className="space-y-4">
                {services.map((service, index) => (
                  <div 
                    key={service.id} 
                    className="border rounded-xl p-6 bg-white flex items-start gap-6"
                    data-testid={`admin-service-${service.id}`}
                  >
                    {/* Sort Buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveService(service.id, 'up')}
                        disabled={index === 0}
                        className="p-1 h-8 w-8"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoveService(service.id, 'down')}
                        disabled={index === services.length - 1}
                        className="p-1 h-8 w-8"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <img 
                      src={service.image_url} 
                      alt={service.name}
                      className="w-32 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900">{service.name}</h3>
                          <p className="text-sm text-slate-600 mt-1">{service.short_description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded">Garanție: {service.warranty}</span>
                            <span className="text-slate-500">{service.phases?.length || 0} etape</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingService(service);
                              setShowServiceDialog(true);
                            }}
                          >
                            <Edit className="w-4 h-4 mr-1" /> Editează
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteService(service.id, service.name)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Șterge
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Service Edit Dialog */}
              <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Editează Serviciu</DialogTitle>
                  </DialogHeader>
                  {editingService && (
                    <div className="space-y-4">
                      <div>
                        <Label>Nume Serviciu</Label>
                        <Input
                          value={editingService.name}
                          onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Descriere Scurtă</Label>
                        <Textarea
                          value={editingService.short_description}
                          onChange={(e) => setEditingService({...editingService, short_description: e.target.value})}
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label>Descriere Completă</Label>
                        <Textarea
                          value={editingService.description}
                          onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>URL Imagine</Label>
                          <Input
                            value={editingService.image_url}
                            onChange={(e) => setEditingService({...editingService, image_url: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Garanție</Label>
                          <Input
                            value={editingService.warranty}
                            onChange={(e) => setEditingService({...editingService, warranty: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="mb-2 block">Etape de Lucru</Label>
                        {editingService.phases?.map((phase, index) => (
                          <div key={index} className="border rounded p-3 mb-2">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="bg-cyan-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm">{phase.order}</span>
                              <Input
                                value={phase.name}
                                onChange={(e) => {
                                  const newPhases = [...editingService.phases];
                                  newPhases[index].name = e.target.value;
                                  setEditingService({...editingService, phases: newPhases});
                                }}
                                placeholder="Nume etapă"
                                className="flex-1"
                              />
                            </div>
                            <Textarea
                              value={phase.description}
                              onChange={(e) => {
                                const newPhases = [...editingService.phases];
                                newPhases[index].description = e.target.value;
                                setEditingService({...editingService, phases: newPhases});
                              }}
                              placeholder="Descriere etapă"
                              rows={2}
                            />
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setShowServiceDialog(false)}>
                          Anulează
                        </Button>
                        <Button onClick={() => handleSaveService(editingService)} className="btn-primary">
                          <Save className="w-4 h-4 mr-1" /> Salvează
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </TabsContent>

            {/* Portfolio Tab */}
            <TabsContent value="portfolio">
              <PortfolioManager token={token} />
            </TabsContent>

            <TabsContent value="materials">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Gestionare Materiale
                </h2>
                <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => setEditingMaterial(null)} data-testid="add-material-btn">
                      <Plus className="mr-2 w-4 h-4" /> Adaugă Material
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-card max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingMaterial ? 'Editează Material' : 'Material Nou'}</DialogTitle>
                    </DialogHeader>
                    <MaterialForm
                      material={editingMaterial}
                      roofTypes={roofTypes}
                      onSave={handleSaveMaterial}
                      onCancel={() => setShowMaterialDialog(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-white/50 rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nume</TableHead>
                      <TableHead>Preț/Unitate</TableHead>
                      <TableHead>Manoperă/Unitate</TableHead>
                      <TableHead>Unitate</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead>Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materials.map(material => (
                      <TableRow key={material.id} data-testid={`material-row-${material.id}`}>
                        <TableCell className="font-medium">{material.name}</TableCell>
                        <TableCell>{material.price_per_unit.toFixed(2)} LEI</TableCell>
                        <TableCell>{material.labor_per_unit.toFixed(2)} LEI</TableCell>
                        <TableCell>{material.unit}</TableCell>
                        <TableCell>{material.category}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDuplicateMaterial(material.id, material.name)}
                              data-testid={`duplicate-material-${material.id}`}
                              title="Duplică material"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingMaterial(material);
                                setShowMaterialDialog(true);
                              }}
                              data-testid={`edit-material-${material.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteMaterial(material.id)}
                              data-testid={`delete-material-${material.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="products">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Gestionare Produse Magazin
                </h2>
                <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" onClick={() => setEditingProduct(null)} data-testid="add-product-btn">
                      <Plus className="mr-2 w-4 h-4" /> Adaugă Produs
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="glass-card max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? 'Editează Produs' : 'Produs Nou'}</DialogTitle>
                    </DialogHeader>
                    <ProductForm
                      product={editingProduct}
                      onSave={handleSaveProduct}
                      onCancel={() => setShowProductDialog(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>

              <div className="bg-white/50 rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nume</TableHead>
                      <TableHead>Preț</TableHead>
                      <TableHead>Stoc</TableHead>
                      <TableHead>Categorie</TableHead>
                      <TableHead>Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map(product => (
                      <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>{product.price.toFixed(2)} LEI</TableCell>
                        <TableCell>{product.stock}</TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingProduct(product);
                                setShowProductDialog(true);
                              }}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteProduct(product.id)}
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const MaterialForm = ({ material, roofTypes, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: material?.name || '',
    price_per_unit: material?.price_per_unit || 0,
    labor_per_unit: material?.labor_per_unit || 0,
    unit: material?.unit || 'mp',
    category: material?.category || '',
    roof_type_id: material?.roof_type_id || '',
    phase_id: material?.phase_id || null,
    is_available: material?.is_available !== undefined ? material.is_available : true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="material-form">
      <div>
        <Label htmlFor="name">Nume Material</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="material-name-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="price">Preț/Unitate (LEI)</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            value={formData.price_per_unit}
            onChange={(e) => setFormData({ ...formData, price_per_unit: parseFloat(e.target.value) })}
            required
            data-testid="material-price-input"
          />
        </div>
        <div>
          <Label htmlFor="labor">Manoperă/Unitate (LEI)</Label>
          <Input
            id="labor"
            type="number"
            step="0.01"
            value={formData.labor_per_unit}
            onChange={(e) => setFormData({ ...formData, labor_per_unit: parseFloat(e.target.value) })}
            required
            data-testid="material-labor-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="unit">Unitate</Label>
          <Input
            id="unit"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            required
            data-testid="material-unit-input"
          />
        </div>
        <div>
          <Label htmlFor="category">Categorie</Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
            data-testid="material-category-input"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="material-cancel-btn">
          Anulează
        </Button>
        <Button type="submit" className="btn-primary" data-testid="material-save-btn">
          <Save className="mr-2 w-4 h-4" /> Salvează
        </Button>
      </div>
    </form>
  );
};

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || 0,
    image_url: product?.image_url || '',
    stock: product?.stock || 0,
    category: product?.category || '',
    unit: product?.unit || 'buc'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
      <div>
        <Label htmlFor="product-name">Nume Produs</Label>
        <Input
          id="product-name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="product-name-input"
        />
      </div>
      <div>
        <Label htmlFor="description">Descriere</Label>
        <Input
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          data-testid="product-description-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="product-price">Preț (LEI)</Label>
          <Input
            id="product-price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
            required
            data-testid="product-price-input"
          />
        </div>
        <div>
          <Label htmlFor="stock">Stoc</Label>
          <Input
            id="stock"
            type="number"
            value={formData.stock}
            onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
            required
            data-testid="product-stock-input"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="image-url">URL Imagine</Label>
        <Input
          id="image-url"
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          required
          data-testid="product-image-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="product-category">Categorie</Label>
          <Input
            id="product-category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
            data-testid="product-category-input"
          />
        </div>
        <div>
          <Label htmlFor="product-unit">Unitate</Label>
          <Input
            id="product-unit"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            required
            data-testid="product-unit-input"
          />
        </div>
      </div>
      <div className="flex justify-end space-x-3">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="product-cancel-btn">
          Anulează
        </Button>
        <Button type="submit" className="btn-primary" data-testid="product-save-btn">
          <Save className="mr-2 w-4 h-4" /> Salvează
        </Button>
      </div>
    </form>
  );
};

export default AdminDashboard;
